import * as React from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext } from 'react-beautiful-dnd';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { errorToast } from '@acceligentllc/storybook';

import LoadingIndicator from 'af-components/LoadingIndicator';

import styles from './styles.module.scss';
import TreeTableBody from './TreeTableBody';
import TreeTableHeader from './TreeTableHeader';
// TODO: uncomment after search functionality is implemented
// import TreeActionsHeader from './TreeActionsHeader';
import type { AttachmentEntity, DirectoryEntity } from './types';

interface DirectoryAttachmentTreeType {
	dataTree: Nullable<DirectoryEntity[]>;
	columns: TypedColumn<DirectoryEntity>[];
	rowActions: RowActions;
	fetchData?: () => void;
	onFileUpload?: (directoryId: number, files: File[]) => void;
	onCopyToWOChange?: (attachmentId: number, copyToWorkOrder: boolean) => void;
	handleAttachmentParentChange: (attachmentId: number, currentParentId: number, futureParentId: number) => void;
	id: Nullable<number>;
	isReadOnly: boolean;
	onAttachmentPreviewModalOpen?: (imageAttachmentSrc: string) => void;
}

interface RowActionOptions {
	actionName: string;
	actionFunction: (original: DirectoryEntity | AttachmentEntity) => void;
	disabled?: boolean;
	show?: boolean;
}

interface RowActions {
	parentDirectory: RowActionOptions[];
	childDirectory: RowActionOptions[];
	attachmentLevel: RowActionOptions[];
}

interface TypedColumn<T> {
	header: string;
	accessor?: string;
	id?: string;
	className?: string;
	width?: number;
	/** We are requiring Cell to be defined so that proper refereces can be pulled in VMs */
	Cell?: (data: Omit<{ original: T; }, 'value'>) => React.ReactNode;
}

// For expandig and collapsing rows
const getAllDirectoriesId = (arrOfIds: number[], dataTree: Nullable<DirectoryEntity[]>) => {

	if (dataTree) {
		dataTree.map((_directory) => {
			if (_directory.directories) {
				getAllDirectoriesId(arrOfIds, _directory.directories);
			}

			arrOfIds.push(_directory.id);
		});
	}

	return arrOfIds;
};

const getDirectoryIds = (dataTree: Nullable<DirectoryEntity[]>) => {
	const ids = [];

	return getAllDirectoriesId(ids, dataTree);
};

const DirectoriesAttachmentTree = (props: DirectoryAttachmentTreeType) => {
	const {
		dataTree,
		columns,
		rowActions,
		fetchData,
		id,
		onFileUpload,
		onCopyToWOChange,
		handleAttachmentParentChange,
		isReadOnly,
		onAttachmentPreviewModalOpen,
	} = props;
	const [arrOfDirectoryIds, setArrOfDirectoryIds] = React.useState<number[]>([]);

	const [visibleRows, setVisibleRows] = React.useState<number[]>(arrOfDirectoryIds);

	const toggleRowVisibility = React.useCallback((rowIds: number[]) => {
		const checkLength = rowIds.length;
		switch (checkLength) {
			case 0:
				return setVisibleRows([]);
			case 1:
				return setVisibleRows((prevVisibleRows) => {
					return prevVisibleRows?.includes(rowIds[0])
						? prevVisibleRows.filter((rowId) => rowId !== rowIds[0])
						: prevVisibleRows ? [...prevVisibleRows, rowIds[0]] : [];
				});
			default:
				return setVisibleRows(arrOfDirectoryIds);
		}
	}, [arrOfDirectoryIds]);

	React.useEffect(() => {
		const dirIds = getDirectoryIds(dataTree);
		setArrOfDirectoryIds(dirIds);
		setVisibleRows(dirIds);
	}, [dataTree]);

	const trimDroppableId = (droppableId: string) => droppableId.split('#').pop();

	const onDragEnd = React.useCallback(({ draggableId, source, destination }: DropResult) => {
		if (!source.droppableId || !destination?.droppableId) {
			const toastId = 'attachment-drop-destination-error';
			errorToast(toastId, { title: 'Destination unknown', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
			return;
		}
		const sourceDirId = trimDroppableId(source.droppableId) ?? null;
		const destinationDirId = trimDroppableId(destination.droppableId) ?? null;

		if (sourceDirId && destinationDirId) {
			handleAttachmentParentChange(+draggableId, +sourceDirId, +destinationDirId);
		} else {
			const toastId = 'attachment-drop-move-error';
			errorToast(toastId, { title: 'Unable to move attachment', timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
		}
	}, [handleAttachmentParentChange]);

	return (
		<div className={`${styles.tree}`}>
			{/* TODO: uncomment after search functionality is implemented */}
			{/* <TreeActionsHeader /> */}
			<TreeTableHeader
				arrOfDirectoryIds={arrOfDirectoryIds}
				columns={columns}
				isReadOnly={isReadOnly}
				rowActions={rowActions}
				toggleRowVisibility={toggleRowVisibility}
				visibleRows={visibleRows}
			/>
			{
				dataTree && dataTree.length > 0 ?
					<DragDropContext onDragEnd={onDragEnd}>
						<TreeTableBody
							columns={columns}
							dataTree={dataTree}
							fetchData={fetchData}
							id={id}
							isReadOnly={isReadOnly}
							onAttachmentPreviewModalOpen={onAttachmentPreviewModalOpen}
							onCopyToWOChange={onCopyToWOChange}
							onFileUpload={onFileUpload}
							rowActions={rowActions}
							toggleRowVisibility={toggleRowVisibility}
							visibleRows={visibleRows}
						/>
					</DragDropContext>
					:
					<div className={`${styles['tree__loading-indicator']}`}>
						<LoadingIndicator color="orange" />
					</div>
			}
		</div>
	);
};

export default React.memo(DirectoriesAttachmentTree);
